/* eslint-disable no-console */
/**
 * A set of util functions to log at appropriate levels for GCP Cloud Functions
 * as it treats all stdout logs as `DEFAULT` and all stderr as `ERROR`.
 * @link https://cloud.google.com/functions/docs/monitoring/logging#writing_runtime_logs
 *
 * Log severity levels are defined in order (lowest to highest):
 * DEFAULT    (0) The log entry has no assigned severity level.
 * DEBUG      (100) Debug or trace information.
 * INFO       (200) Routine information, such as ongoing status or performance.
 * NOTICE     (300) Normal but significant events, such as start up, shut down,
 *            or a configuration change.
 * WARNING    (400) Warning events might cause problems.
 * ERROR      (500) Error events are likely to cause problems.
 * CRITICAL   (600) Critical events cause more severe problems or outages.
 * ALERT      (700) A person must take an action immediately.
 * EMERGENCY  (800) One or more systems are unusable.
 * @link https://cloud.google.com/logging/docs/reference/v2/rest/v2/LogEntry#logseverity
 */
const globalLogFields = {};
/**
 * Sets the trace key for any corresponding uses of the logging.
 *
 * Should be retrieved from the `X-Cloud-Trace-Context` header, which should be
 * int the format `TRACE_ID/SPAN_ID;o=TRACE_TRUE`.
 * For example:
 * `X-Cloud-Trace-Context: 105445aa7843bc8bf206b12000100000/1;o=1`
 *
 * @link https://cloud.google.com/trace/docs/setup#force-trace
 *
 * @param traceId key to use for tracing the logs
 */
const initialize = (traceId) => {
    globalLogFields["logging.googleapis.com/trace"] =
        `projects/${process.env.GCP_PROJECT_ID}/traces/${traceId}`;
};
const info = (logMessage) => {
    console.log(JSON.stringify({ severity: "INFO", ...globalLogFields, ...logMessage }));
};
const debug = (logMessage) => {
    console.log(JSON.stringify({ severity: "DEBUG", ...globalLogFields, ...logMessage }));
};
const notice = (logMessage) => {
    console.log(JSON.stringify({ severity: "NOTICE", ...globalLogFields, ...logMessage }));
};
const warning = (logMessage) => {
    console.log(JSON.stringify({ severity: "WARNING", ...globalLogFields, ...logMessage }));
};
const error = (logMessage) => {
    console.log(JSON.stringify({ severity: "ERROR", ...globalLogFields, ...logMessage }));
};
const critical = (logMessage) => {
    console.log(JSON.stringify({ severity: "CRITICAL", ...globalLogFields, ...logMessage }));
};
const alert = (logMessage) => {
    console.log(JSON.stringify({ severity: "ALERT", ...globalLogFields, ...logMessage }));
};
const emergency = (logMessage) => {
    console.log(JSON.stringify({ severity: "EMERGENCY", ...globalLogFields, ...logMessage }));
};
export default {
    initialize,
    info,
    debug,
    notice,
    warning,
    error,
    critical,
    alert,
    emergency
};
//# sourceMappingURL=data:application/json;base64,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